/**
 * navController
 * -------------
 * This library provides a reliable control mechanism and
 * interface for the primary navigation menu.
 */
import getCssProp from 'DOM/getCssProp';
import { isDomEl } from 'DOM/domTypeChecking';
import { createFocusTrap } from 'focus-trap';

// The state object
const state = {
  navs: [],
  mode: 'mobile',
  toggle: null,
  navWrapper: null,
  primaryNav: null,
  focusTrap: null,
  activeNav: null,
  isOpen: false,
  open: {
    primary: false,
    secondary: false,
    tertiary: false,
  },
  lastAction: null,
  closeQueue: [],
};

// DOM Selectors
const selectors = {
  primaryNav: '[data-nav="primary"]',
  primaryNavToggle: '[data-primary-nav-toggle]',
  navWrapper: '[data-nav-wrapper]',
  nav: '[data-nav]',
  subNavParent: '.subnav-parent',
  subNavParentLink: '.subnav-parent > .nav-link',
  subNavTrigger: '[data-subnav-trigger]',
  closeButton: '[data-nav-close]',
};

// Config Object
let config = {
  closeDelay: 500,
  activeClass: 'active',
  subNavActiveClass: 'subnav-active',
};

const touchLastAction = () => {
  state.lastAction = Date.now();
};

/**
 * Remove an index from the closeQueue
 */
const removeFromCloseQueue = (index) => {
  const newCloseQueue = state.closeQueue.filter((arrayElement) => arrayElement !== index);
  state.closeQueue = newCloseQueue;
};

/**
 * Add a nav index to the closeQueue (to be closed after delay)
 */
const addToCloseQueue = (index) => {
  if (!state.closeQueue.includes(index)) {
    state.closeQueue.push(index);
  }
};

/**
 * Get the subnav menu for an element
 */
const getSubNav = (target) => {
  const element = isDomEl(target) ? target : target.currentTarget || target.target;
  if (!element) return false;

  return element.querySelector(selectors.nav);
};

/**
 * Get the parent nav for a given element
 */
const getParentNav = (element) => {
  const parentNav = element.parentNode.closest(selectors.nav);
  return parentNav;
};

/**
 * Retrieve the current nav mode (mobile or desktop)
 */
const getNavMode = () => {
  const navMode = getCssProp(state.primaryNav, '--nav-mode');

  state.mode = navMode;
  state.navWrapper.dataset.navMode = navMode;

  return navMode;
};

/**
 * Update the nav wrapper element
 */
const updateWrapper = () => {
  getNavMode();
  if (state.isOpen) {
    state.navWrapper.classList.add(config.activeClass);
    state.navWrapper.classList.add(config.subNavActiveClass);
    state.toggle.classList.add(config.activeClass);
    state.toggle.setAttribute('aria-expanded', true);
  } else {
    state.navWrapper.classList.remove(config.activeClass);
    state.navWrapper.classList.remove(config.subNavActiveClass);
    state.toggle.classList.remove(config.activeClass);
    state.toggle.setAttribute('aria-expanded', false);
  }
};

/**
 * Open a nav menu
 */
const open = (nav) => {
  const { navIndex } = nav.dataset;

  nav.classList.add(config.activeClass);
  state.isOpen = true;
  state.navs[navIndex].isOpen = true;
  state.navs[navIndex].focusTrap.activate();
  state.activeNav = nav;
  if (state.navs[navIndex].trigger) {
    state.navs[navIndex].trigger.setAttribute('aria-expanded', true);
  }
  updateWrapper();
  touchLastAction();
};

/**
 * Close a nav menu
 */
const close = (nav) => {
  const { navIndex } = nav.dataset;

  nav.classList.remove(config.activeClass);
  state.navs[navIndex].isOpen = true;
  state.navs[navIndex].focusTrap.deactivate();
  if (state.navs[navIndex].trigger) {
    state.navs[navIndex].trigger.setAttribute('aria-expanded', false);
  }

  if (state.mode === 'desktop' && nav.dataset.nav === 'secondary') {
    state.isOpen = false;
    state.activeNav = null;
  } else {
    state.activeNav = getParentNav(nav);
  }

  removeFromCloseQueue(navIndex);
  updateWrapper();
  touchLastAction();
};

/**
 * Toggle a subnav open state
 */
const toggleNav = (event, force = false) => {
  const trigger = event.currentTarget || event.target;
  const subNav = getSubNav(trigger.closest(selectors.subNavParent));

  if (force === 'open') {
    open(subNav);
  } else if (force === 'close') {
    open(subNav);
  } else if (subNav.classList.contains(config.activeClass)) {
    close(subNav);
  } else {
    open(subNav);
  }
};

/**
 * Toggle the overall nav wrapper
 */
const toggleNavWrapper = () => {
  if (state.isOpen) {
    // Nav is open, close nav
    state.isOpen = false;
    updateWrapper();
    state.focusTrap.deactivate();
  } else {
    // Nav is closed, open nav
    state.isOpen = true;
    updateWrapper();
    state.focusTrap.activate();
  }
};

/**
 * Complete a deferred close
 */
const deferredClose = (index) => {
  if (state.closeQueue.includes(index)) {
    close(state.navs[index].element);
  }
};

/**
 * Set up a deferred close (for when exiting a nav)
 */
const setupDeferredClose = (index) => {
  addToCloseQueue(index);
  setTimeout(deferredClose, config.closeDelay, index);
};

// Keypress Handler
const keyHandler = (event) => {
  if (event.key === 'Escape') {
    const activeNavLevel = state.activeNav && state.activeNav.dataset.nav;

    if (state.isOpen) {
      if (state.activeNav === null || activeNavLevel === 'primary') {
        toggleNavWrapper();
      } else {
        close(state.activeNav);
      }
    }
  }
};

/**
 * Add all addEventListeners required for the nav
 */
const addEventListeners = () => {
  const subNavTriggers = document.querySelectorAll(selectors.subNavTrigger);
  const closeButtons = document.querySelectorAll(selectors.closeButton);
  const subNavParents = document.querySelectorAll(selectors.subNavParent);
  const subNavParentLinks = document.querySelectorAll(selectors.subNavParentLink);

  // Add toggle listener
  if (state.toggle) {
    state.toggle.addEventListener('click', toggleNavWrapper);
  }

  // Add toggle & close button click listeners
  subNavTriggers.forEach((element) => {
    element.addEventListener('click', toggleNav);
  });

  closeButtons.forEach((element) => {
    element.addEventListener('click', () => {
      const parentNav = getParentNav(element);
      close(parentNav);
    });
  });

  // Add listeners for mouseenter and mouseleave on all navs
  state.navs.forEach((nav, index) => {
    nav.element.addEventListener('mouseenter', () => {
      state.navs[index].isHovered = true;

      removeFromCloseQueue(index);
    });

    nav.element.addEventListener('mouseleave', () => {
      state.navs[index].isHovered = false;
    });
  });

  // Add listeners for hovering subnav parent links
  subNavParentLinks.forEach((element) => {
    element.addEventListener('mouseover', (event) => {
      getNavMode();

      if (state.mode !== 'mobile') {
        toggleNav(event, 'open');
      }
    });
  });

  // Add listeners for hovering subnav parent li's
  subNavParents.forEach((element) => {
    const childNav = element.querySelector(selectors.nav);
    const index = childNav.dataset.navIndex;

    element.addEventListener('mouseenter', () => {
      removeFromCloseQueue(index);
    });

    element.addEventListener('mouseleave', () => {
      setupDeferredClose(index);
    });
  });

  // Add keypress listener
  document.addEventListener('keydown', keyHandler, {
    capture: true,
    passive: false,
  });
};

/**
 * Map the nav structure
 */
const mapNav = () => {
  const allNavs = document.querySelectorAll(selectors.nav);
  let navIndex = 1;

  // Walk all navs, and create a map in state
  allNavs.forEach((nav) => {
    state.navs[navIndex] = {
      element: nav,
      level: nav.dataset.nav,
      isHovered: false,
      isOpen: false,
      parent: null,
      children: [],
      focusTrap: createFocusTrap(nav, { allowOutsideClick: true }),
      trigger: nav.parentNode.querySelector(selectors.subNavTrigger),
    };

    // Set index as a data-attribute on the element
    nav.setAttribute('data-nav-index', navIndex);
    navIndex += 1;
  });

  state.navs.forEach((nav) => {
    const parentNav = nav.element.parentNode.closest(selectors.nav);
    if (!parentNav) return nav;

    const mutatedNav = nav;
    mutatedNav.parent = parentNav.dataset.navIndex;

    return mutatedNav;
  });
};

/**
 * Initiate the nav controller.
 * You may optionally pass an options object that will be merged
 * with the default options.
 */
const init = (options) => {
  config = { ...config, ...options };

  // Find nav elements in the DOM
  state.navWrapper = document.querySelector(selectors.navWrapper);
  state.primaryNav = document.querySelector(selectors.primaryNav);
  state.toggle = document.querySelector(selectors.primaryNavToggle);

  if (!state.primaryNav) return;

  // Map the nav, setup focus traps, event listeners, etc.
  const wrapperFocusOptions = {
    allowOutsideClick: true,
    escapeDeactivates: false,
  };
  state.focusTrap = createFocusTrap(state.navWrapper, wrapperFocusOptions);
  mapNav();
  getNavMode();
  addEventListeners();
};

/**
 * Controller for handling nav functionality
 */
const navController = {
  init,
};

export default navController;
